import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Tag, Select, Switch, notification, theme, Radio, Popconfirm } from 'antd';
import { DiventryBlock } from '../../../Templates/Layout';
import { LogTable } from '../../../Templates/Logs';
import { LockOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from "react-i18next";

const { useToken } = theme;

export const LogsColors = {
    blocking: "4px solid #a14838",
    detecting: "4px solid #e58900",
    passed: "4px solid #22d933",
    bypassing: "4px solid #4974a5",
}

export const policies = {
    blocking: "✅ Mode de blocage (Journaliser & bloquer)",
    detecting: "🕵️‍♂️ Mode de détection (Journaliser uniquement)",
    bypassing: "⚠️ Mode de contournement (Pas de journalisation & pas de blocage)",
}

const options = [
    { label: 'Admin', value: 'admin' },
    { label: 'Utilisateur', value: 'user' },
];

// export const policies = {
//     blocking: t("Security.blocking"),
//     detecting: t("Security.detecting"),
//     bypassing: t("Security.bypassing"),
// }

// const options = [
//     { label: t("Security.admin"), value: 'admin' },
//     { label: t("Security.user"), value: 'user' },
// ];


export function SecurityPolicies({ kernel, type }) {
    const { t } = useTranslation(); // Utilisation de la traduction
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const [loaded, setLoaded] = useState(true);
    const [loading, setLoading] = useState(false);

    const { token: design } = useToken();
    const [info, setInfo] = useState({});
    const [infoTags, setInfoTags] = useState({});
    const [expands, setExpands] = useState([]);
    const [securityLocked, setSecurityLocked] = useState([]);
    const [waitingConfirmation, setWaitingConfirmation] = useState(false);

    // async load
    useEffect(() => {
        async function fetch() {

            // fetch tags
            const tagsRes = await kernel.api.get(`/dohzel/tags`);
            if (tagsRes.error) {
                notification.error({
                    message: t('Security.ErrorFetchingInfo'), // Utilisation de la traduction
                    description: tagsRes.error
                });

                return;
            }

            // fetch info configuration
            var current = await kernel.api.get(`/dohzel/${type}/get?id=${params.id}`);
            if (current.error) {
                notification.error({
                    message: t('Security.ErrorFetchingInfo'),
                    description: current.error
                });
                return;
            }

            // merge tag and info configuration
            const newProfileTags = {};
            for (var tag of tagsRes.data) {
                newProfileTags[tag.name] = {
                    ...tag,
                    activate: false,
                    locked: false
                };
            }
            if (!Array.isArray(current.data.security))
                current.data.security = [];
            for (var tags of current.data.security) {
                if (!newProfileTags[tags])
                    continue;
                newProfileTags[tags].activate = true;
            }

            if (!Array.isArray(current.data.securityLocked))
                current.data.securityLocked = [];
            for (var tags of current.data.securityLocked) {
                if (!newProfileTags[tags])
                    continue;
                newProfileTags[tags].locked = true;
            }

            if (current.data?.profileId && type == 'device') {
                // fetch info profile
                var profile = await kernel.api.get(`/dohzel/profile/get?id=${current.data?.profileId}`);
                setSecurityLocked(profile?.data?.securityLocked);
            }

            setInfo(current.data);

            const final = {}
            const rebuild = Object.values(newProfileTags)?.sort((a, b) => a?.order - b?.order)
            for (var item of rebuild)
                final[item.name] = item
            setInfoTags(final);
        }
        fetch();
    }, [params.id, loaded]);

    async function switchButton(item) {
        setLoading(true);
        const data = {
            id: info.id,
            security: [],
            securityLocked: []
        };

        infoTags[item.name].activate = !infoTags[item.name].activate;
        for (var key in infoTags) {
            const value = infoTags[key];
            if (value.activate === true)
                data.security.push(key);

            if (value.locked === true)
                data.securityLocked.push(key);
        }

        const ret = await kernel.api.post(`/dohzel/${type}/update`, data);
        if (!ret || ret.error) {
            notification.error({
                message: t('Security.UpdateError'), // Utilisation de la traduction
                description: ret.error
            });
            setLoaded(!loaded);
            setLoading(false);
            return;
        }

        setLoaded(!loaded);
        setLoading(false);
    }

    const lockButton = async (item) => {
        setLoading(true);
        const data = {
            id: info.id,
            security: [],
            securityLocked: []
        };
        infoTags[item.name].locked = !infoTags[item.name].locked;
        for (var key in infoTags) {
            const value = infoTags[key];
            if (value.locked === true)
                data.securityLocked.push(key);

            if (value.activate === true)
                data.security.push(key);
        }

        const ret = await kernel.api.post(`/dohzel/${type}/update`, data);
        if (!ret || ret.error) {
            notification.error({
                message: t('Security.UpdateError'),
                description: ret.error
            });
            return;
        }

        setLoaded(!loaded);
        setLoading(false);
    };

    async function changePolicy(mode) {
        const data = {
            id: info.id,
            policy: mode
        };

        const ret = await kernel.api.post(`/dohzel/${type}/update`, data);
        if (!ret || ret.error) {
            notification.error({
                message: t('Security.UpdateError'),
                description: ret.error
            });
            return;
        }

        notification.success({
            message: t('Security.SecurityPolicyChanged'), // Utilisation de la traduction
            description: t("Security." + mode)
        });

        setLoaded(!loaded);
    }

    const handleExpan = (entry) => {
        const item = expands?.find((expansion) => expansion === entry.id)
        if (item) {
            setExpands(expands?.filter((expansion) => expansion !== entry.id));
        }
        else {
            setExpands([...expands, entry?.id]);
        }
    }

    return (
        <>
            <DiventryBlock>
                <p>
                    <Select
                        value={info.policy}
                        style={{ minWidth: 300 }}
                        onChange={(key) => {
                            changePolicy(key);
                        }}
                        options={Object.keys(policies).map((key) => {
                            return ({
                                value: key,
                                label: t(`Security.${key}`)
                            });
                        })} />
                </p>
                <LogTable
                    kernel={kernel}
                    data={
                        [
                            {
                                id: 'title',
                                open: false,
                                left: <>
                                    <span style={{ fontSize: 20, fontWeight: 700, marginLeft: 55 }}>{t('Security.Policy')}</span> {/* Traduction */}
                                </>,
                                right: <>
                                    <div style={{ display: "flex", alignItems: "center", float: 'right', gap: (type === "profile" ? "70px" : '140px'), marginLeft: 250 }}>
                                        <span style={{ fontSize: 20, fontWeight: 700 }}>{t('Security.Management')}</span> {/* Traduction */}
                                        <span style={{ fontSize: 20, fontWeight: 700 }}>{t('Security.Status')}</span> {/* Traduction */}
                                    </div>
                                </>,
                            },
                            ...Object.values(infoTags).map((item) => {
                                return ({
                                    id: item.name,
                                    open: !!expands?.find((expansion) => expansion === item.name),
                                    handleExpan: handleExpan,
                                    left: <>
                                        <Tag color={item.color} style={{
                                            padding: 5,
                                            fontSize: 18,
                                            fontWeight: 700
                                        }}>#{item.name}</Tag>
                                        <strong>{item.brief}</strong>
                                    </>,
                                    right: <>
                                        <div style={{ display: "flex", alignItems: "center", float: 'right', gap: (type === "profile" ? "100px" : '200px'), marginLeft: 250 }}>

                                            {/* Radio Button Group or Icon */}
                                            {type === "profile" ? (
                                                <Radio.Group
                                                    block
                                                    size="small"
                                                    options={options?.map((item) => ({ label: t("Security." + item.value), value: item.value }))}
                                                    onChange={(val) => {
                                                        lockButton(item);
                                                    }}
                                                    defaultValue={item.locked ? "admin" : "user"}
                                                    optionType="button"
                                                    buttonStyle="solid"
                                                />
                                            ) : (
                                                <div style={{ marginRight: 25 }}>
                                                    {
                                                        securityLocked?.includes(item.name) ? <LockOutlined /> : <>&ensp;&ensp;</>
                                                    }
                                                </div>
                                            )}
                                            {/* Switch */}
                                            <Popconfirm
                                                title={t('Security.securityRemoveConfirmTitle')}
                                                description={t('Security.securityRemoveConfirmContent')}
                                                open={item.activate && item.name == 'security' && waitingConfirmation}
                                                onConfirm={() => {
                                                    switchButton(item);
                                                    setWaitingConfirmation(false);
                                                }}
                                                onCancel={() => setWaitingConfirmation(false)}
                                                okText={t('YesConfirmation')}
                                                cancelText={t('NoConfirmation')}
                                            >
                                                <Switch
                                                    disabled={securityLocked?.includes(item.name)}
                                                    loading={loading}
                                                    value={item.activate}
                                                    onClick={() => {
                                                        if (item.activate && item.name == 'security') {
                                                            setWaitingConfirmation(true)
                                                        }
                                                        else {
                                                            switchButton(item);
                                                        }
                                                    }}
                                                />
                                            </Popconfirm>

                                        </div>
                                    </>
                                    ,
                                    expand: item.description ? item.description : null
                                });
                            })
                        ]
                    }
                />
            </DiventryBlock>
        </>
    );
}
